<script lang="ts">
	/**
	 * WorkflowBeat — one beat of the home's "Five things" narrative.
	 *
	 * Single typographic header per beat: the title. The proof number +
	 * source weaves into the opening of the body sentence — no separate
	 * eyebrow row, no separate verb row. Brand restraint: one strong
	 * title, then prose, then exhibit.
	 *
	 *   <WorkflowBeat
	 *     index="01"
	 *     title="Prior auth, end-to-end."
	 *     body="Thirteen hours per physician per week (AMA 2024). …"
	 *   >
	 *     <ExhibitPaQueue />
	 *   </WorkflowBeat>
	 */

	import type { Snippet } from 'svelte';

	interface Props {
		/** Two-character pad: "01", "02", … */
		index: string;
		/** The single title. Starts with the 1–2 most important words for a practice manager. */
		title: string;
		/** Body paragraph — opens with the number + source, then names what Ekavi does. */
		body: string;
		/** Flip text / exhibit columns on desktop */
		reverse?: boolean;
		children: Snippet;
	}

	let { index, title, body, reverse = false, children }: Props = $props();
</script>

<section class="beat" class:beat-reverse={reverse}>
	<div class="beat-text">
		<p class="beat-index" aria-hidden="true">
			<span class="beat-num">{index}</span>
			<span class="beat-rule"></span>
		</p>
		<h3 class="beat-title">{title}</h3>
		<p class="beat-body">{body}</p>
	</div>
	<div class="beat-exhibit">
		{@render children()}
	</div>
</section>

<style>
	/* Single column on narrow viewports — text always reads first.
	   At ≥720px the beat splits text | exhibit in a 5/7 ratio so the
	   exhibit (data-rich) gets the breathing room it needs. The reverse
	   variant flips the visual order without changing source order. */
	.beat {
		display: grid;
		grid-template-columns: 1fr;
		gap: var(--ekavi-space-9);
		padding-block: var(--ekavi-space-12);
		align-items: center;
	}
	@media (min-width: 720px) {
		.beat {
			grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
			column-gap: var(--ekavi-space-18);
			padding-block: var(--ekavi-space-18);
		}
		.beat-reverse {
			grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
		}
		.beat-reverse .beat-text {
			order: 2;
		}
		.beat-reverse .beat-exhibit {
			order: 1;
		}
	}

	/* Quiet beat counter (01, 02, …) with a hairline rule running off
	   the right of the number. Anchors each beat without competing
	   with the title. */
	.beat-index {
		display: flex;
		align-items: center;
		gap: var(--ekavi-space-3);
		margin: 0 0 var(--ekavi-space-6);
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		color: var(--ekavi-ink-3);
		font-weight: var(--ekavi-weight-halbfett);
		font-variant-numeric: tabular-nums;
	}
	.beat-num {
		flex-shrink: 0;
	}
	.beat-rule {
		flex: 1;
		height: 1px;
		background: var(--ekavi-hairline);
	}

	/* The single title — quieter than the previous kraftig treatment.
	   Halbfett (500) holds the weight of a heading without shouting. */
	.beat-title {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-halbfett);
		font-size: clamp(1.375rem, 3vw, 1.75rem);
		letter-spacing: var(--ekavi-track-headers);
		line-height: 1.2;
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-3);
		max-width: 22ch;
		text-wrap: balance;
	}

	.beat-body {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		line-height: 1.6;
		color: var(--ekavi-ink-2);
		margin: 0;
		max-width: 38ch;
		text-wrap: pretty;
	}

	.beat-exhibit {
		min-width: 0;
	}
</style>
